import { get, isNumber, map } from 'lodash'

import { centsToDollars } from '../utils/formatters'

export const DEFAULT_EXEMPTIONS = {
  applyDiscount: true,
  applySalesTax: true,
  applyServiceFee: true,
}

// adds display attributes for fees, subtotal, and total
export const formatOrderTotals = (args = {}) => {
  const {
    discountAmountInCents,
    lineItems,
    promotions,
    serviceChargeInCents,
    taxAmountInCents,
    tipAmountInCents,
    totalAmountInCents,
  } = args

  const serviceChargeLineItem = lineItems.find((li) => {
    return li.specialType === 'service_charge' || li.specialType === 'taxable_service_charge'
  })
  const serviceChargeTaxInCents = parseFloat(serviceChargeLineItem?.taxAmountInCents ?? 0)
  const serviceChargeDiscountInCents = parseFloat(serviceChargeLineItem?.promotions?.[0]?.discountAmountInCents ?? 0)
  const serviceChargeName = serviceChargeLineItem?.name ?? 'Service Charge'

  const safeTipCents = tipAmountInCents || 0
  const fees = discountAmountInCents + serviceChargeInCents + taxAmountInCents + safeTipCents
  const subtotalInCents = totalAmountInCents - fees - Math.abs(serviceChargeDiscountInCents)
  const promotionParamsForClient = makeClientPromotionParams(promotions)
  const taxesByExemptions = parseExemptableTaxes(lineItems)

  return {
    ...args,
    discountDisplay: centsToDollars(discountAmountInCents),
    promotionParamsForClient,
    serviceChargeInCents,
    serviceChargeTaxInCents,
    serviceChargeDiscountInCents,
    serviceChargeName,
    serviceChargeDisplay: centsToDollars(serviceChargeInCents),
    subtotalDisplay: centsToDollars(subtotalInCents),
    subtotalInCents,
    taxDisplay: centsToDollars(taxAmountInCents),
    totalDisplay: centsToDollars(totalAmountInCents),
    ...taxesByExemptions,
  }
}

// adds display attributes for fees, subtotal, and total
export const formatOrderDetails = (args = {}) => {
  const {
    discountAmountInCents,
    lineItems,
    promotions,
    serviceChargeInCents,
    taxAmountInCents,
    tipAmountInCents,
    totalAmountInCents,
  } = args

  const serviceChargeLineItem = lineItems.find((li) => {
    return li.itemSpecialType === 'service_charge' || li.itemSpecialType === 'taxable_service_charge'
  })
  const serviceChargeTaxInCents = parseFloat(serviceChargeLineItem?.taxAmountInCents ?? 0)
  const serviceChargeName = serviceChargeLineItem?.name ?? 'Service Charge'

  const safeTipCents = tipAmountInCents || 0
  const fees = discountAmountInCents + serviceChargeInCents + taxAmountInCents + safeTipCents
  const subtotalInCents = totalAmountInCents - fees
  const promotionParamsForClient = makeClientPromotionParams(promotions)
  const taxesByExemptions = parseExemptableTaxes(lineItems)

  return {
    ...args,
    discountDisplay: centsToDollars(discountAmountInCents),
    promotionParamsForClient,
    serviceChargeDisplay: centsToDollars(serviceChargeInCents),
    serviceChargeTaxInCents,
    serviceChargeName,
    subtotalDisplay: centsToDollars(subtotalInCents),
    subtotalInCents,
    taxDisplay: centsToDollars(taxAmountInCents),
    totalDisplay: centsToDollars(totalAmountInCents),
    ...taxesByExemptions,
  }
}

export const makeClientPromotionParams = (promotions = []) => {
  return map(promotions, (p) => {
    return { uuid: get(p, 'uuid') }
  })
}

export const makeExemptions = (orderTotal, orderingPreference) => {
  const discountAmountInCents = get(orderTotal, 'discountAmountInCents', null)
  const serviceChargeInCents = get(orderTotal, 'serviceChargeInCents', null)

  const exemptions = {
    applyDiscount: isNumber(discountAmountInCents),
    applyServiceFee: isNumber(serviceChargeInCents),
    applySalesTax: !orderingPreference.taxExemptBool,
  }

  return exemptions
}

// remote order total might not take exemptions into consideration
// currently just need to handle tax, but could be extended if/when needed
export const makeTotalDisplayWithExemptions = (exemptions, remoteOrderTotal, defaultTotal) => {
  let displayTotal = get(remoteOrderTotal, 'totalAmountInCents', defaultTotal)

  if (!get(exemptions, 'applySalesTax', false)) displayTotal -= get(remoteOrderTotal, 'exemptableTaxAmountInCents', 0)

  return centsToDollars(displayTotal)
}

export const parseExemptableTaxes = (lineItems = []) => {
  let exemptableTaxRules = {}
  let exemptableTaxAmountInCents = 0
  let nonExemptableTaxRules = {}
  let nonExemptableTaxAmountInCents = 0

  lineItems.forEach(lineItem => {
    const taxRules = get(lineItem, 'taxRules') || []

    taxRules.forEach(taxRule => {
      const uuid = get(taxRule, 'taxRuleUuid', 'unknown')
      const taxInCents = get(taxRule, 'taxAmountInCents', 0)

      const isExempt = get(taxRule, 'exempt', false)
      isExempt ? exemptableTaxAmountInCents += taxInCents : nonExemptableTaxAmountInCents += taxInCents

      let collection = isExempt ? exemptableTaxRules : nonExemptableTaxRules
      const oldRuleData = get(collection, uuid, {})
      const newRuleData = {
        ...oldRuleData,
        ...taxRule,
        lineItemCount: get(oldRuleData, 'lineItemCount', 0) + 1,
        taxAmountInCents: taxInCents + get(oldRuleData, 'taxAmountInCents', 0),
      }

      collection[uuid] = newRuleData
    })
  })

  return {
    exemptableTaxRules,
    exemptableTaxAmountInCents,
    exemptableTaxDisplay: centsToDollars(exemptableTaxAmountInCents),
    nonExemptableTaxRules,
    nonExemptableTaxAmountInCents,
    nonExemptableTaxDisplay: centsToDollars(nonExemptableTaxAmountInCents),
  }
}

export const resolveExemptionOverrideFlags = ({ exemptionOverrides, promotions }) => {
  return {
    exemptServiceFee: !exemptionOverrides.applyServiceFee,
    promotions: exemptionOverrides.applyDiscount ? promotions : [],
    taxExempt: !exemptionOverrides.applySalesTax,
  }
}
